import { type CountryCode, type ProjectCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCampaignProducts, useApiMutationCampaignProducts } from '@hooks-api/fortune-fastpass/use-api-campaign-products'
import { type Nullable } from '@hooks-api/types/nullable'
import { useEffect, useMemo } from 'react'
import { parseProductMap } from '../parser/parseProductMap'
import { type CartItem } from '../types'
import { createDedupeProductIdsHandlers } from '../utils/createDedupeProductIdsHandlers'

const { removeDedupeProductIds, updateAndGetDedupeCartItemProductIdsSet, parseCartItemProductIdsSet } = createDedupeProductIdsHandlers()

export interface UseCampaignProductsInfoProps {
  cartItems: CartItem[]
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignCode: Nullable<string>
  merchantId: Nullable<number>
  extraProductIds?: CartItem['cartItemId'][]
}

export const useCampaignProductsInfo = (
  props: UseCampaignProductsInfoProps,
) => {
  const { cartItems, projectCode, countryCode, campaignCode, merchantId, extraProductIds } = props
  const { trigger: getProducts } = useApiMutationCampaignProducts()
  /**
   * unmount 時清除 extraProductIds
   * Note: deps 用 stringify 判斷，防止因為是 array 的 reference 改變而執行 callback
   */
  useEffect(() => {
    return () => {
      removeDedupeProductIds(extraProductIds || [])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(extraProductIds)])

  const { productIds } = useMemo(() => {
    const { productIdsSet } = updateAndGetDedupeCartItemProductIdsSet(cartItems, extraProductIds)
    return {
      productIds: [...productIdsSet].join(','),
    }
  }, [cartItems, extraProductIds])

  const { destructuredData: products, mutate, isLoading: isProductsLoading } = useApiCampaignProducts({
    project_code: projectCode,
    country_code: countryCode,
    campaign_code: campaignCode,
    merchant_id: merchantId,
    product_ids: productIds,
  }, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  })

  const productMap = useMemo(() => parseProductMap(products), [products])

  /**
   * 重新取得商品資料，並透過 mutate 更新 swr cache
   */
  const revalidateProductsInfo = useCallbackEvent(async (extraProductIds?: CartItem['cartItemId'][]) => {
    const { productIdsSet } = parseCartItemProductIdsSet(cartItems, extraProductIds)
    const res = await getProducts({
      project_code: projectCode,
      country_code: countryCode,
      campaign_code: campaignCode,
      merchant_id: merchantId,
      product_ids: [...productIdsSet].join(','),
    })
    const newProducts = res.data.data
    let nextProductMap = { ...productMap }
    newProducts.forEach((product) => {
      nextProductMap = {
        ...nextProductMap,
        [product.id]: product,
      }
    })

    mutate({
      ...res,
      data: {
        ...res.data,
        data: Object.values(nextProductMap).filter(product => product !== undefined),
      },
    }, {
      revalidate: false,
    })
    return res.data.data
  })

  const getProductsInfo = useCallbackEvent(async (extraProductIds?: CartItem['cartItemId'][]) => {
    const { productIdsSet } = parseCartItemProductIdsSet(cartItems, extraProductIds)
    const { data } = await getProducts({
      project_code: projectCode,
      country_code: countryCode,
      campaign_code: campaignCode,
      product_ids: [...productIdsSet].join(','),
      merchant_id: merchantId,
    })
    return data.data
  })

  return {
    productMap,
    revalidateProductsInfo,
    getProductsInfo,
    isProductsLoading,
    getProducts,
  }
}
